// The React shell's adapter over `BrainAccount` (@merkury/brain, docs/09 §9).
//
// This class used to BE the web's account implementation — a second one beside
// the brain's, drifting apart from it a fix at a time. It is now a thin façade:
// every ceremony is the brain's, and what remains here is the handful of things
// that are genuinely web-UX-shaped and have no place in a shared brain:
//
//   - completeOnboarding, because the React onboarding flow generates the
//     identity itself and hands it over finished (see adoptIdentity);
//   - the pending avatar seed, because only the authed runtime (ReadyApp) has a
//     RestClient to PATCH it with;
//   - the passkey-restore contract the restore SCREEN expects: resolve
//     undefined for "ran, nothing to restore" and let real ceremony errors
//     through so the copy can distinguish them.
//
// The React context (accountContext) only mirrors this class's state.

import type { PasskeySummary } from '@merkury/protocol';
import type { RestClient } from '@merkury/brain/api/client.js';
import type { IdentityKeys } from '@merkury/brain/identity/keys.js';
import {
  BrainAccount,
  PasskeyRestoreError,
  RegistrationPendingError,
} from '@merkury/brain/runtime/account.js';
import type { StoredAccount } from '@merkury/brain/storage/accountStore.js';
import { isCapacitorNative } from './capacitor/platform.js';
import type { OnboardingResult } from '../onboarding/types.js';

export interface LoadedAccount {
  account: StoredAccount;
  identity: IdentityKeys;
}

export class AccountService {
  constructor(private readonly brain: BrainAccount) {}

  /**
   * Restore the E2E identity on a NEW device from a synced passkey (docs/03 §2,
   * PRF pattern). No `userId` ⇒ the one-tap DISCOVERABLE flow (the
   * authenticator picks the account); with `userId` the legacy targeted flow
   * (old per-user-salt blobs).
   *
   * The brain LANDS the account itself — identity persisted, session kept — so
   * unlike the pre-merge web path there is no re-register afterwards. The keys
   * come back so onboarding can finish exactly like the recovery-phrase path.
   *
   * Undefined ⇒ the ceremony ran but there was nothing to restore (no PRF
   * output, no ciphertext, no matching passkey); the caller falls back to the
   * recovery phrase. On the WEB a real ceremony failure (dismissed sheet, wrong
   * origin, no WebAuthn) PROPAGATES so the restore screen can show
   * distinguishable copy; on NATIVE it is swallowed to undefined — the Compose
   * UI drives its own ceremony and has no distinguishable-error surface to feed.
   */
  async restoreWithPasskey(userId?: string): Promise<IdentityKeys | undefined> {
    try {
      const ready = await this.brain.restorePasskey(userId);
      return ready.identity;
    } catch (err) {
      if (err instanceof PasskeyRestoreError) return undefined;
      if (isCapacitorNative()) return undefined;
      throw err;
    }
  }

  /**
   * Warm the passkey-restore options ahead of the click so `restoreWithPasskey`
   * runs the ceremony with no preceding network await (Safari's transient
   * activation window). Fire-and-forget; failures fall back to fetch-in-tap.
   */
  primePasskeyRestore(): Promise<void> {
    return this.brain.primePasskeyRestore();
  }

  /** Restores local state on launch; undefined means onboarding is required. */
  async load(): Promise<LoadedAccount | undefined> {
    const loaded = await this.brain.load();
    // A loaded account with no userId is a signed-in user whose registration
    // never got through (offline onboarding); the brain is already retrying it
    // in the background, and the app stays browsable meanwhile.
    return loaded ? { account: loaded.account, identity: loaded.identity } : undefined;
  }

  /**
   * Avatar index chosen during onboarding (JW11): persisted locally with the
   * account below, but the server PATCH needs the authenticated RestClient,
   * which only exists once ReadyApp builds the session services — the authed
   * runtime drains this exactly once via takePendingAvatarSeed(). Lost on a
   * failed PATCH (offline): acceptable — the settings picker is the fallback.
   */
  private pendingAvatarSeed: number | undefined;

  takePendingAvatarSeed(): number | undefined {
    const seed = this.pendingAvatarSeed;
    this.pendingAvatarSeed = undefined;
    return seed;
  }

  /**
   * Land the identity the onboarding flow built: persist, register, log in.
   *
   * Offline-tolerant by construction (the brain is built with
   * `offlineProvision`): when the server cannot be reached the brain still
   * persists the identity and reports `RegistrationPendingError` carrying the
   * local account, which we treat as success — the user gets a usable app and
   * the registration retries on the next launch. Two things surface instead:
   * `PasswordRequiredError`, which onboarding answers with its password prompt,
   * and ANY failure while the user is actively submitting a password —
   * swallowing a rate limit there completed onboarding without a session and
   * stranded the user at a yellow dot (field report 2026-07-16).
   */
  async completeOnboarding(result: OnboardingResult): Promise<StoredAccount> {
    try {
      const ready = await this.brain.adoptIdentity(result.identity, {
        displayName: result.displayName,
        restored: result.restored,
        ...(result.avatarSeed !== undefined ? { avatarSeed: result.avatarSeed } : {}),
        ...(result.restorePassword ? { password: result.restorePassword } : {}),
      });
      if (result.avatarSeed !== undefined) this.pendingAvatarSeed = result.avatarSeed;
      return ready.account;
    } catch (err) {
      if (err instanceof RegistrationPendingError && !result.restorePassword) {
        if (result.avatarSeed !== undefined) this.pendingAvatarSeed = result.avatarSeed;
        return err.account;
      }
      throw err;
    }
  }

  /**
   * Access token for REST/WS, refreshing single-flight and re-minting a session
   * with the identity key rather than ever dead-ending while the device holds
   * the identity (docs/03 §3, prime directive). Bounded as a whole by the
   * brain's `tokenBudgetMs`. Throws only when re-login genuinely fails
   * (offline, deleted account, stale password proof).
   */
  getAccessToken(): Promise<string> {
    return this.brain.getAccessToken();
  }

  /**
   * Persist the chosen avatar seed onto the local account (offline-first: the
   * `PATCH /me` that sent it to the server already ran bridge-side). Returns the
   * updated account so the context can mirror it into the native snapshot, or
   * undefined when nothing is stored yet. A no-op — same account back — when the
   * seed is unchanged, so a `GET /me` reconciliation never triggers a re-render.
   */
  setAvatarSeed(seed: number): Promise<StoredAccount | undefined> {
    return this.brain.setAvatarSeed(seed);
  }

  /** Persist / clear the stored password authKey (2FA proof) after a password
   *  change driven OUTSIDE the brain — the Capacitor bridge's `identity.
   *  setPassword`/`removePassword`, until B3-android moves them over. The web
   *  settings screens go through the brain directly and never call this. */
  setStoredAuthKey(authKey: Uint8Array | undefined): Promise<void> {
    return this.brain.setStoredAuthKey(authKey);
  }

  async signOut(): Promise<void> {
    await this.brain.signOut();
  }

  // --- credential-guarded ceremonies (docs/03 §2, §3a, §3b) ------------------
  // These need the authenticated RestClient, which only the authed runtime
  // has, so it is passed per call rather than held here.

  /** Set or change the account password; persists the fresh 2FA proof. */
  setPassword(rest: RestClient, password: string, terminateOthers: boolean): Promise<void> {
    return this.brain.setPassword(rest, password, terminateOthers);
  }

  /** Remove the account password; clears the stored 2FA proof. */
  removePassword(rest: RestClient, terminateOthers: boolean): Promise<void> {
    return this.brain.removePassword(rest, terminateOthers);
  }

  /** Permanently delete the account, then drop every local trace of it. */
  deleteAccount(rest: RestClient, password?: string): Promise<void> {
    return this.brain.deleteAccount(rest, password);
  }

  /** Warm the passkey REGISTRATION challenge before the tap (Safari activation). */
  primeAddPasskey(rest: RestClient): Promise<void> {
    return this.brain.primeAddPasskey(rest);
  }

  /**
   * Register a passkey for this account. `restorable` false ⇒ the authenticator
   * has no PRF, so it signs in but cannot restore a new device. `null` ⇒ the
   * ceremony produced no credential, which is not an error.
   */
  addPasskey(
    rest: RestClient,
    label?: string,
  ): Promise<{ passkey: PasskeySummary; restorable: boolean } | null> {
    return this.brain.addPasskey(rest, label);
  }
}
